// src/pages/fiscal/Dashboard/DashboardNotas.tsx
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-restricted-globals */
import { useEffect, useMemo, useState } from 'react';
import {
  Box, Grid, Paper, Typography, useTheme, alpha, Card, CardContent, ButtonGroup, Button, Chip, Pagination,
  useMediaQuery,
  Container,
  Popover,
  IconButton,
  Alert,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import {
  XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, ComposedChart, Area, Line,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import DescriptionIcon from '@mui/icons-material/Description';
import SummarizeIcon from '@mui/icons-material/Summarize';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import 'dayjs/locale/pt-br';

import { LayoutBaseDeDashboard } from '../../../shared/layouts';
import {
  GraficosNotasService,
  IDashboardNotasGraficos,
  IGraficoNotasItem,
  ITopProdutoItem,
  TPeriodoNotas,
} from '../../../shared/services/api/Fiscal/GraficosNotasService';

dayjs.locale('pt-br');
dayjs.extend(utc);

type TModoExibicao = 'detalhado' | 'total' | 'compravenda';
type TModoRosca = 'compravenda' | 'notas';

interface PontoNotas extends IGraficoNotasItem {
  label: string;
}

const formatCurrency = (value: number) => {
  return value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
};

const formatQuantity = (value: number) => {
  return value.toLocaleString('pt-BR');
};

interface ICustomTooltipProps {
  active?: boolean;
  payload?: Array<{ payload: PontoNotas }>;
  label?: string;
}

const CustomTooltip: React.FC<ICustomTooltipProps> = ({ active, payload, label }) => {
  const theme = useTheme();

  if (!active || !payload || !payload.length) return null;

  const item = payload[0].payload as PontoNotas;

  return (
    <Paper
      sx={{
        p: 1.5,
        borderRadius: 2,
        boxShadow: theme.shadows[4],
        minWidth: 190,
      }}
    >
      <Typography variant="caption" fontWeight="bold" sx={{ display: 'block', mb: 0.5 }}>
        {label}
      </Typography>

      <Box display="flex" justifyContent="space-between" gap={2}>
        <Typography variant="caption" sx={{ color: theme.palette.primary.main }}>
          NF-e
        </Typography>
        <Typography variant="caption" fontWeight="bold">
          {formatCurrency(item.valornfe)} ({item.qtdenfe})
        </Typography>
      </Box>

      <Box display="flex" justifyContent="space-between" gap={2}>
        <Typography variant="caption" sx={{ color: theme.palette.warning.main }}>
          NFC-e
        </Typography>
        <Typography variant="caption" fontWeight="bold">
          {formatCurrency(item.valornfce)} ({item.qtdenfce})
        </Typography>
      </Box>

      <Box
        display="flex"
        justifyContent="space-between"
        gap={2}
        mt={0.5}
        pt={0.5}
        borderTop={`1px solid ${alpha(theme.palette.divider, 0.2)}`}
      >
        <Typography variant="caption" sx={{ color: theme.palette.success.main }} fontWeight="bold">
          Vendas
        </Typography>
        <Typography variant="caption" fontWeight="bold">
          {formatCurrency(item.valortotal)} ({item.qtdetotal})
        </Typography>
      </Box>

      <Box display="flex" justifyContent="space-between" gap={2}>
        <Typography variant="caption" sx={{ color: theme.palette.info.main }}>
          Compras
        </Typography>
        <Typography variant="caption" fontWeight="bold">
          {formatCurrency(item.valorcompra)} ({item.qtdecompra})
        </Typography>
      </Box>

      <Box
        display="flex"
        justifyContent="space-between"
        gap={2}
        mt={0.5}
        pt={0.5}
        borderTop={`1px solid ${alpha(theme.palette.divider, 0.2)}`}
      >
        <Typography variant="caption" fontWeight="bold">
          Saldo
        </Typography>
        <Typography
          variant="caption"
          fontWeight="bold"
          sx={{ color: item.saldo >= 0 ? theme.palette.success.main : theme.palette.error.main }}
        >
          {formatCurrency(item.saldo)}
        </Typography>
      </Box>
    </Paper>
  );
};

interface IRankingProdutosProps {
  titulo: string;
  icon: React.ReactNode;
  cor: string;
  itens: ITopProdutoItem[];
  metrica: 'quantidade' | 'valor';
}

const ITENS_POR_PAGINA = 5;

const RankingProdutos: React.FC<IRankingProdutosProps> = ({ titulo, icon, cor, itens, metrica }) => {
  const theme = useTheme();
  const [pagina, setPagina] = useState(1);

  useEffect(() => {
    setPagina(1);
  }, [itens]);

  const valorPrincipal = (item: ITopProdutoItem) => (metrica === 'valor' ? item.valor : item.quantidade);
  const maiorValor = itens[0] ? valorPrincipal(itens[0]) : 1;

  const totalPaginas = Math.max(1, Math.ceil(itens.length / ITENS_POR_PAGINA));
  const itensPagina = itens.slice((pagina - 1) * ITENS_POR_PAGINA, pagina * ITENS_POR_PAGINA);

  return (
    <Paper
      sx={{
        p: 2,
        overflow: 'hidden',
        width: '100%',
        boxSizing: 'border-box',
        borderRadius: 3,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        gap={1}
        mb={2}
        pb={1}
        borderBottom={`2px solid ${alpha(cor, 0.2)}`}
      >
        {icon}

        <Typography
          variant="subtitle2"
          fontWeight="bold"
          sx={{ flex: 1 }}
        >
          {titulo}
        </Typography>

        <Chip
          label={`${itens.length} produtos`}
          size="small"
          sx={{ bgcolor: alpha(cor, 0.12), color: cor, fontWeight: 'bold' }}
          variant="outlined"
        />
      </Box>

      {itens.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">
            Nenhum produto encontrado
          </Typography>
        </Box>
      )}

      {itensPagina.map((item, index) => (
        <Box
          key={item.idproduto}
          sx={{
            p: 1.5,
            borderRadius: 2,
            mb: 1.5,
            bgcolor: alpha(theme.palette.background.paper, 0.4),
            transition: 'all 0.2s',
            cursor: 'default',
            '&:hover': {
              bgcolor: alpha(cor, 0.08),
              transform: 'translateX(4px)',
            },
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={1.5} flex={1} sx={{ minWidth: 0 }}>
              <Chip
                label={`#${(pagina - 1) * ITENS_POR_PAGINA + index + 1}`}
                size="small"
                sx={{ fontWeight: 'bold', minWidth: 40, bgcolor: cor, color: '#fff' }}
              />

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  noWrap
                  title={item.descricao ?? undefined}
                >
                  {item.descricao || 'Produto sem descrição'}
                </Typography>

                <Typography variant="caption" color="text.secondary">
                  {item.ean || 'Sem EAN'}
                </Typography>
              </Box>
            </Box>

            <Box textAlign="right" sx={{ flexShrink: 0, pl: 1 }}>
              <Typography variant="body2" fontWeight="bold" sx={{ color: cor }}>
                {metrica === 'valor' ? formatCurrency(item.valor) : `${formatQuantity(item.quantidade)} un.`}
              </Typography>

              <Typography variant="caption" color="text.secondary">
                {metrica === 'valor' ? `${formatQuantity(item.quantidade)} un.` : formatCurrency(item.valor)}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              mt: 1,
              height: 3,
              borderRadius: 1,
              bgcolor: alpha(theme.palette.grey[500], 0.2),
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                width: `${(valorPrincipal(item) / (maiorValor || 1)) * 100}%`,
                height: '100%',
                bgcolor: cor,
                borderRadius: 1,
                transition: 'width 0.3s',
              }}
            />
          </Box>
        </Box>
      ))}

      {itens.length > 0 && (
        <Box
          display="flex"
          justifyContent="center"
          mt="auto"
          pt={1}
          borderTop={`1px solid ${alpha(theme.palette.divider, 0.1)}`}
        >
          <Pagination
            page={pagina}
            onChange={(_, value) => setPagina(value)}
            count={totalPaginas}
            size="small"
            color="primary"
          />
        </Box>
      )}
    </Paper>
  );
};

export const DashboardNotas: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [isLoading, setIsLoading] = useState(false);
  const [msgType, setMsgType] = useState<'success' | 'error' | 'warning' | 'info'>('error');
  const [msg, setMsg] = useState('');
  const [openMsg, setOpenMsg] = useState(false);

  const [periodo, setPeriodo] = useState<TPeriodoNotas>('atual');
  const [modoExibicao, setModoExibicao] = useState<TModoExibicao>('detalhado');
  const [modoRosca, setModoRosca] = useState<TModoRosca>('compravenda');
  const [ano, setAno] = useState<number | undefined>(undefined);

  const [anchorAno, setAnchorAno] = useState<HTMLElement | null>(null);
  const [anoPopover, setAnoPopover] = useState(dayjs().year());

  const [dados, setDados] = useState<IDashboardNotasGraficos | null>(null);

  const abrirSeletorAno = (target: HTMLElement) => {
    if (periodo !== 'mensal') return;

    setAnoPopover(ano ?? dayjs().year());
    setAnchorAno(target);
  };

  useEffect(() => {
    const carregarDashboard = async () => {
      setIsLoading(true);

      const result = await GraficosNotasService.getDashboardNotasGraficos(
        periodo,
        periodo === 'mensal' ? ano : undefined,
      );

      setIsLoading(false);

      if (result instanceof Error) {
        setMsgType('error');
        setMsg(result.message);
        setOpenMsg(true);
      } else {
        setDados(result);
      }
    };

    carregarDashboard();
  }, [periodo, ano]);

  useEffect(() => {
    setModoRosca(periodo === 'atual' ? 'compravenda' : 'notas');
  }, [periodo]);

  const totais = dados?.totais ?? {
    valornfe: 0,
    qtdenfe: 0,
    valornfce: 0,
    qtdenfce: 0,
    valortotal: 0,
    qtdetotal: 0,
    valorcompra: 0,
    qtdecompra: 0,
    saldo: 0,
  };

  const intervaloFormatado = useMemo(() => {
    if (!dados?.intervalo) return '';

    return `${dayjs(dados.intervalo.datainicial).format('DD/MM/YY')} — ${dayjs(dados.intervalo.datafinal).format('DD/MM/YY')}`;
  }, [dados?.intervalo]);

  const chartData = useMemo<PontoNotas[]>(() => {
    const agrupamento = dados?.agrupamento ?? (periodo === 'atual' ? 'dia' : 'mes');

    return (dados?.grafico ?? []).map((item) => ({
      ...item,
      label: agrupamento === 'dia'
        ? dayjs.utc(item.data).format('DD/MM')
        : dayjs.utc(item.data).format('MMM/YY'),
    }));
  }, [dados?.grafico, dados?.agrupamento, periodo]);

  const dadosRosca = useMemo(() => {
    if (modoRosca === 'compravenda') {
      return [
        { name: 'Vendas', value: totais.qtdetotal, valor: totais.valortotal, color: theme.palette.success.main },
        { name: 'Compras', value: totais.qtdecompra, valor: totais.valorcompra, color: theme.palette.info.main },
      ];
    }

    return [
      { name: 'NF-e', value: totais.qtdenfe, valor: totais.valornfe, color: theme.palette.primary.main },
      { name: 'NFC-e', value: totais.qtdenfce, valor: totais.valornfce, color: theme.palette.warning.main },
    ];
  }, [modoRosca, totais.qtdenfe, totais.qtdenfce, totais.valornfe, totais.valornfce, totais.qtdetotal, totais.valortotal, totais.qtdecompra, totais.valorcompra, theme]);

  const totalValorRosca = dadosRosca.reduce((acc, item) => acc + item.valor, 0);

  return (
    <LayoutBaseDeDashboard
      barraDeFerramentas={<Box />}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: '100%',
          overflowX: 'hidden',
          overflowY: 'auto',
          bgcolor: theme.palette.background.default,
          minHeight: '100vh',
          boxSizing: 'border-box',
        }}
      >
        <Container
          maxWidth={false}
          disableGutters
          sx={{
            width: '100%',
            maxWidth: '100%',
            overflowX: 'hidden',
            px: { xs: 1, sm: 2 },
            boxSizing: 'border-box',
          }}
        >
          {/* CARDS */}
          <Grid
            container
            spacing={2}
            sx={{
              width: '100%',
              m: 0,
              mb: 2,
            }}
          >
            <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
              <Card
                onClick={(e) => abrirSeletorAno(e.currentTarget)}
                sx={{
                  borderRadius: 3,
                  height: '100%',
                  cursor: periodo === 'mensal' ? 'pointer' : 'default',
                  opacity: periodo === 'mensal' ? 1 : 0.75,
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': periodo === 'mensal' ? {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[8],
                  } : undefined,
                }}
              >
                <CardContent sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    width="100%"
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 500 }}
                      >
                        Período
                      </Typography>

                      <Box display="flex" alignItems="center" sx={{ mt: 1 }}>
                        <Typography
                          variant="h6"
                          fontWeight="bold"
                          sx={{ lineHeight: 1.2 }}
                        >
                          {periodo === 'atual' ? 'Mês Atual' : (ano ?? 'Automático')}
                        </Typography>

                        {periodo === 'mensal' && (
                          <ArrowDropDownIcon
                            sx={{ color: theme.palette.info.main }}
                          />
                        )}
                      </Box>

                      {intervaloFormatado && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', mt: 0.5 }}
                        >
                          {intervaloFormatado}
                        </Typography>
                      )}
                    </Box>

                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.info.main, 0.1),
                      }}
                    >
                      <CalendarTodayIcon
                        sx={{
                          fontSize: 32,
                          color: theme.palette.info.main
                        }}
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              <Popover
                open={Boolean(anchorAno)}
                anchorEl={anchorAno}
                onClose={() => setAnchorAno(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              >
                <Box sx={{ width: 220, p: 2 }}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={1.5}
                  >
                    <IconButton
                      size="small"
                      onClick={() => setAnoPopover((valor) => valor - 1)}
                    >
                      <ChevronLeftIcon fontSize="small" />
                    </IconButton>

                    <Typography variant="subtitle2" fontWeight="bold">
                      {anoPopover}
                    </Typography>

                    <IconButton
                      size="small"
                      onClick={() => setAnoPopover((valor) => valor + 1)}
                    >
                      <ChevronRightIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  <Button
                    fullWidth
                    size="small"
                    variant="contained"
                    color="primary"
                    sx={{ mb: 1 }}
                    onClick={() => {
                      setAno(anoPopover);
                      setAnchorAno(null);
                    }}
                  >
                    Filtrar {anoPopover}
                  </Button>

                  <Button
                    fullWidth
                    size="small"
                    variant={ano === undefined ? 'contained' : 'outlined'}
                    color="secondary"
                    onClick={() => {
                      setAno(undefined);
                      setAnchorAno(null);
                    }}
                  >
                    Automático (últimos 12 meses)
                  </Button>
                </Box>
              </Popover>
            </Grid>

            <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
              <Card
                sx={{
                  borderRadius: 3,
                  height: '100%',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[8],
                  }
                }}
              >
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 500 }}
                      >
                        Total NF-e
                      </Typography>

                      <Typography
                        variant="h5"
                        fontWeight="bold"
                        sx={{ mt: 1, color: theme.palette.primary.main }}
                      >
                        {formatCurrency(totais.valornfe)}
                      </Typography>

                      <Typography
                        variant="caption"
                        color="text.secondary"
                      >
                        {totais.qtdenfe} notas
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                      }}
                    >
                      <DescriptionIcon
                        sx={{
                          fontSize: 32,
                          color: theme.palette.primary.main
                        }}
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
              <Card
                sx={{
                  borderRadius: 3,
                  height: '100%',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[8],
                  }
                }}
              >
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 500 }}
                      >
                        Total NFC-e
                      </Typography>

                      <Typography
                        variant="h5"
                        fontWeight="bold"
                        sx={{ mt: 1, color: theme.palette.warning.main }}
                      >
                        {formatCurrency(totais.valornfce)}
                      </Typography>

                      <Typography
                        variant="caption"
                        color="text.secondary"
                      >
                        {totais.qtdenfce} notas
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.warning.main, 0.1),
                      }}
                    >
                      <ReceiptLongIcon
                        sx={{
                          fontSize: 32,
                          color: theme.palette.warning.main
                        }}
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
              <Card
                sx={{
                  borderRadius: 3,
                  height: '100%',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[8],
                  }
                }}
              >
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 500 }}
                      >
                        Total Geral
                      </Typography>

                      <Typography
                        variant="h5"
                        fontWeight="bold"
                        sx={{ mt: 1, color: theme.palette.success.main }}
                      >
                        {formatCurrency(totais.valortotal)}
                      </Typography>

                      <Typography
                        variant="caption"
                        color="text.secondary"
                      >
                        {totais.qtdetotal} notas
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.success.main, 0.1),
                      }}
                    >
                      <SummarizeIcon
                        sx={{
                          fontSize: 32,
                          color: theme.palette.success.main
                        }}
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* GRAFICOS */}
          <Grid
            container
            spacing={2}
            sx={{
              width: '100%',
              m: 0,
              pb: 3,
            }}
          >
            <Grid item xs={12} lg={9} sx={{ minWidth: 0 }}>
              <Paper
                sx={{
                  p: 2,
                  overflow: 'hidden',
                  width: '100%',
                  boxSizing: 'border-box',
                  borderRadius: 3,
                }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  flexWrap="wrap"
                  gap={1}
                  mb={2}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                  >
                    Evolução de Notas
                  </Typography>

                  <Box display="flex" flexWrap="wrap" gap={1}>
                    <ButtonGroup size="small">
                      <Button
                        variant={periodo === 'atual' ? 'contained' : 'outlined'}
                        onClick={() => setPeriodo('atual')}
                      >
                        Atual
                      </Button>

                      <Button
                        variant={periodo === 'mensal' ? 'contained' : 'outlined'}
                        onClick={() => setPeriodo('mensal')}
                      >
                        Mensal
                      </Button>
                    </ButtonGroup>

                    <ButtonGroup size="small">
                      <Button
                        variant={modoExibicao === 'detalhado' ? 'contained' : 'outlined'}
                        onClick={() => setModoExibicao('detalhado')}
                      >
                        NF-e / NFC-e
                      </Button>

                      <Button
                        variant={modoExibicao === 'total' ? 'contained' : 'outlined'}
                        onClick={() => setModoExibicao('total')}
                      >
                        Total
                      </Button>

                      <Button
                        variant={modoExibicao === 'compravenda' ? 'contained' : 'outlined'}
                        onClick={() => setModoExibicao('compravenda')}
                      >
                        Compra / Venda
                      </Button>
                    </ButtonGroup>
                  </Box>
                </Box>

                <Box
                  sx={{
                    width: '100%',
                    height: isMobile ? 260 : 360,
                    position: 'relative',
                  }}
                >
                  {isLoading && (
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1,
                      }}
                    >
                      <CircularProgress size={28} />
                    </Box>
                  )}

                  <ResponsiveContainer
                    width="100%"
                    height="100%"
                  >
                    <ComposedChart data={chartData}>
                      <defs>
                        <linearGradient
                          id="gradientTotalNotas"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor={theme.palette.success.main}
                            stopOpacity={0.8}
                          />

                          <stop
                            offset="95%"
                            stopColor={theme.palette.success.main}
                            stopOpacity={0}
                          />
                        </linearGradient>

                        <linearGradient
                          id="gradientVendasCV"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor={theme.palette.success.main}
                            stopOpacity={0.7}
                          />

                          <stop
                            offset="95%"
                            stopColor={theme.palette.success.main}
                            stopOpacity={0}
                          />
                        </linearGradient>

                        <linearGradient
                          id="gradientComprasCV"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor={theme.palette.info.main}
                            stopOpacity={0.7}
                          />

                          <stop
                            offset="95%"
                            stopColor={theme.palette.info.main}
                            stopOpacity={0}
                          />
                        </linearGradient>
                      </defs>

                      <CartesianGrid strokeDasharray="3 3" />

                      <XAxis dataKey="label" />

                      <YAxis
                        tickFormatter={(value) =>
                          value.toLocaleString('pt-BR', {
                            notation: 'compact',
                          })
                        }
                      />

                      <RechartsTooltip content={<CustomTooltip />} />

                      <Legend />

                      {modoExibicao === 'detalhado' && (
                        <>
                          <Line
                            type="monotone"
                            dataKey="valornfe"
                            name="NF-e"
                            stroke={theme.palette.primary.main}
                            strokeWidth={2}
                            dot={{ r: 3, fill: theme.palette.primary.main }}
                            activeDot={{ r: 6 }}
                          />

                          <Line
                            type="monotone"
                            dataKey="valornfce"
                            name="NFC-e"
                            stroke={theme.palette.warning.main}
                            strokeWidth={2}
                            dot={{ r: 3, fill: theme.palette.warning.main }}
                            activeDot={{ r: 6 }}
                          />
                        </>
                      )}

                      {modoExibicao === 'total' && (
                        <Area
                          type="monotone"
                          dataKey="valortotal"
                          name="Total"
                          stroke={theme.palette.success.main}
                          fill="url(#gradientTotalNotas)"
                          strokeWidth={2}
                        />
                      )}

                      {modoExibicao === 'compravenda' && (
                        <>
                          <Area
                            type="monotone"
                            dataKey="valortotal"
                            name="Vendas"
                            stroke={theme.palette.success.main}
                            fill="url(#gradientVendasCV)"
                            strokeWidth={2}
                          />

                          <Area
                            type="monotone"
                            dataKey="valorcompra"
                            name="Compras"
                            stroke={theme.palette.info.main}
                            fill="url(#gradientComprasCV)"
                            strokeWidth={2}
                          />
                        </>
                      )}
                    </ComposedChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>

            {/* GRAFICO ROSCA */}
            <Grid item xs={12} lg={3} sx={{ minWidth: 0 }}>
              <Paper
                sx={{
                  p: 2,
                  overflow: 'hidden',
                  width: '100%',
                  boxSizing: 'border-box',
                  borderRadius: 3,
                  height: '100%',
                }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  flexWrap="wrap"
                  gap={1}
                  mb={2}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                  >
                    {modoRosca === 'compravenda' ? 'Compras x Vendas' : 'NF-e x NFC-e'}
                  </Typography>

                  <ButtonGroup size="small">
                    <Button
                      variant={modoRosca === 'notas' ? 'contained' : 'outlined'}
                      onClick={() => setModoRosca('notas')}
                    >
                      NF-e / NFC-e
                    </Button>

                    <Button
                      variant={modoRosca === 'compravenda' ? 'contained' : 'outlined'}
                      onClick={() => setModoRosca('compravenda')}
                    >
                      Compra / Venda
                    </Button>
                  </ButtonGroup>
                </Box>

                <Box
                  sx={{
                    width: '100%',
                    height: 180,
                  }}
                >
                  <ResponsiveContainer
                    width="100%"
                    height="100%"
                  >
                    <PieChart>
                      <Pie
                        data={dadosRosca}
                        dataKey="valor"
                        innerRadius={35}
                        outerRadius={55}
                        paddingAngle={3}
                      >
                        {dadosRosca.map((item, index) => (
                          <Cell
                            key={index}
                            fill={item.color}
                          />
                        ))}
                      </Pie>

                      <RechartsTooltip
                        formatter={(value: any, name: any, entry: any) => {
                          const qtd = entry?.payload?.value ?? 0;
                          const percentual = totalValorRosca > 0 ? ((Number(value) / totalValorRosca) * 100).toFixed(1) : '0';

                          return [
                            `${formatCurrency(Number(value))} · ${qtd} notas · ${percentual}%`,
                            name,
                          ];
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>

                <Box mt={1}>
                  {dadosRosca.map((item) => (
                    <Box
                      key={item.name}
                      sx={{ mb: 1.5 }}
                    >
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={1}
                        >
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              bgcolor: item.color,
                            }}
                          />

                          <Typography variant="caption">
                            {item.name}
                          </Typography>
                        </Box>

                        <Typography
                          variant="caption"
                          fontWeight="bold"
                          sx={{ color: item.color }}
                        >
                          {totalValorRosca > 0 ? ((item.valor / totalValorRosca) * 100).toFixed(1) : '0'}%
                        </Typography>
                      </Box>

                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', pl: 2.25 }}
                      >
                        {item.value} {modoRosca === 'compravenda' ? 'itens' : 'notas'} · {formatCurrency(item.valor)}
                      </Typography>
                    </Box>
                  ))}

                  {totalValorRosca === 0 && (
                    <Typography variant="caption" color="text.secondary">
                      Nenhum dado encontrado
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* TOP PRODUTOS */}
          <Grid
            container
            spacing={2}
            sx={{
              width: '100%',
              m: 0,
              pb: 3,
            }}
          >
            <Grid item xs={12} lg={6} sx={{ minWidth: 0 }}>
              <RankingProdutos
                titulo="Produtos em Notas (Quantidade)"
                icon={<Inventory2Icon sx={{ color: theme.palette.info.main }} />}
                cor={theme.palette.info.main}
                itens={dados?.topProdutosQuantidade ?? []}
                metrica="quantidade"
              />
            </Grid>

            <Grid item xs={12} lg={6} sx={{ minWidth: 0 }}>
              <RankingProdutos
                titulo="Produtos em Notas (Valor)"
                icon={<TrendingUpIcon sx={{ color: theme.palette.secondary.main }} />}
                cor={theme.palette.secondary.main}
                itens={dados?.topProdutosValor ?? []}
                metrica="valor"
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Snackbar
        open={openMsg}
        autoHideDuration={6000}
        onClose={() => setOpenMsg(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setOpenMsg(false)} severity={msgType} sx={{ width: '100%' }}>
          {msg}
        </Alert>
      </Snackbar>
    </LayoutBaseDeDashboard>
  );
};
